import { z } from 'zod';

import i18n from '@/lib/i18n';

import type { ApplicationData, Licensing } from '../types/application';
import type { ValidationMessage } from '../types/applicationApi.types';

// Helper schemas
const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
const zipRegex = /^\d{5}$/;

const FL_LOCATIONS = new Set(['miami-dade', 'broward', 'palm-beach', 'orlando', 'tampa']);
const NY_LOCATIONS = new Set(['manhattan', 'brooklyn', 'queens', 'bronx', 'staten-island']);

// Step A: Personal Info
export const stepASchema = z.object({
  firstName: z.string().min(1, i18n.t('validation.fields.firstName')),
  middleName: z.string().optional(),
  lastName: z.string().min(1, i18n.t('validation.fields.lastName')),
  preferredName: z.string().optional(),
  email: z.string().email(i18n.t('validation.fields.email')),
  phone: z.string().regex(phoneRegex, i18n.t('validation.fields.phone')),
  address: z.string().min(1, i18n.t('validation.fields.address')),
  city: z.string().min(1, i18n.t('validation.fields.city')),
  state: z.string().min(2, i18n.t('validation.fields.state')),
  zip: z.string().regex(zipRegex, i18n.t('validation.fields.zip')),
  language: z.enum(['English', 'Spanish']),
});

// Step B: Eligibility
export const stepBSchema = z.object({
  isOver18: z.boolean().refine((val) => val === true, {
    message: i18n.t('validation.fields.isOver18'),
  }),
  isAuthorizedToWork: z
    .boolean()
    .optional()
    .refine((val) => val !== undefined, {
      message: i18n.t('validation.fields.isAuthorizedToWork'),
    }),
});

// Step C: Licensing - with state-specific validation
export const floridaLicensingSchema = z.object({
  hasClassD: z.boolean(),
  classDNumber: z.string().optional(),
  classDExpiration: z.string().optional(),
  hasClassG: z.boolean(),
  classGNumber: z.string().optional(),
  classGExpiration: z.string().optional(),
});

export const newYorkLicensingSchema = z.object({
  hasGuardCard: z.boolean(),
  guardCardNumber: z.string().optional(),
  guardCardExpiration: z.string().optional(),
  has8HourCert: z.boolean(),
  cert8HourDate: z.string().optional(),
  has16HourCert: z.boolean(),
  cert16HourDate: z.string().optional(),
  hasFirearmCreds: z.boolean(),
  firearmCredsNumber: z.string().optional(),
  firearmCredsExpiration: z.string().optional(),
});

export const commonCertificationsSchema = z.object({
  hasCprAed: z.boolean(),
  cprAedExpiration: z.string().optional(),
  hasTwic: z.boolean(),
  twicExpiration: z.string().optional(),
});

// Step D: Availability
export const stepDSchema = z.object({
  roles: z.array(z.string()).min(1, i18n.t('validation.fields.roles')),
  locations: z.array(z.string()).min(1, i18n.t('validation.fields.locations')),
  shifts: z.array(z.string()).min(1, i18n.t('validation.fields.shifts')),
  hasTransport: z.boolean(),
  startDate: z.string().min(1, i18n.t('validation.fields.startDate')),
});

// Step E: Preferences
export const stepESchema = z.object({
  indoorOutdoorComfort: z.number().min(1).max(5),
  standingPatrolTolerance: z.number().min(1).max(5),
  languagesSpoken: z.array(z.string()).min(1, i18n.t('validation.fields.languagesSpoken')),
  isVeteran: z.boolean().optional(),
  eeoRace: z.string().optional(),
  eeoGender: z.string().optional(),
  eeoDisclosureConsent: z.boolean(),
});

// Step F: Consents
export const stepFSchema = z.object({
  consentPrivacy: z.boolean().refine((val) => val === true, {
    message: i18n.t('validation.fields.consentPrivacy'),
  }),
  consentMessaging: z.boolean().refine((val) => val === true, {
    message: i18n.t('validation.fields.consentMessaging'),
  }),
  consentVideoRecording: z.boolean().refine((val) => val === true, {
    message: i18n.t('validation.fields.consentVideoRecording'),
  }),
  signature: z.string().min(1, i18n.t('validation.fields.signature')),
  signatureDate: z.string().min(1, i18n.t('validation.fields.signatureDate')),
});

// Custom validation for Step C based on state
export function validateStepC(data: Licensing, state: string): { success: boolean; errors: Record<string, string> } {
  if (data.licensingNotApplicable) {
    return { success: true, errors: {} };
  }

  const errors: Record<string, string> = {};

  if (state === 'FL') {
    // Florida requires Class D license
    if (!data.florida?.hasClassD) {
      errors.licenses = i18n.t('validation.stepC.flClassDRequired');
    } else {
      if (!data.florida.classDNumber) {
        errors.classDNumber = i18n.t('validation.stepC.classDNumber');
      }
      if (!data.florida.classDExpiration) {
        errors.classDExpiration = i18n.t('validation.stepC.classDExpiration');
      }
    }
    // If Class G is selected, validate its fields
    if (data.florida?.hasClassG) {
      if (!data.florida.classGNumber) {
        errors.classGNumber = i18n.t('validation.stepC.classGNumber');
      }
      if (!data.florida.classGExpiration) {
        errors.classGExpiration = i18n.t('validation.stepC.classGExpiration');
      }
    }
  }

  if (state === 'NY') {
    // New York requires Guard Card
    if (!data.newYork?.hasGuardCard) {
      errors.licenses = i18n.t('validation.stepC.nyGuardCardRequired');
    } else {
      if (!data.newYork.guardCardNumber) {
        errors.guardCardNumber = i18n.t('validation.stepC.guardCardNumber');
      }
      if (!data.newYork.guardCardExpiration) {
        errors.guardCardExpiration = i18n.t('validation.stepC.guardCardExpiration');
      }
    }
    // If firearm creds selected, validate
    if (data.newYork?.hasFirearmCreds) {
      if (!data.newYork.firearmCredsNumber) {
        errors.firearmCredsNumber = i18n.t('validation.stepC.firearmCredsNumber');
      }
      if (!data.newYork.firearmCredsExpiration) {
        errors.firearmCredsExpiration = i18n.t('validation.stepC.firearmCredsExpiration');
      }
    }
  }

  return {
    success: Object.keys(errors).length === 0,
    errors,
  };
}

export function validateCrossStepRules(data: ApplicationData): ValidationMessage[] {
  const errors: ValidationMessage[] = [];
  const roles = data.stepD.roles;
  const locations = data.stepD.locations;
  const hasArmedRole = roles.includes('armed');
  const hasFlLocations = locations.some((location) => FL_LOCATIONS.has(location));
  const hasNyLocations = locations.some((location) => NY_LOCATIONS.has(location));

  if (!data.stepC.licensingNotApplicable) {
    if (hasFlLocations && !data.stepC.florida?.hasClassD) {
      errors.push({
        field: 'florida.hasClassD',
        message: i18n.t('validation.crossStep.flClassDRequired'),
      });
    }

    if (hasNyLocations && !data.stepC.newYork?.hasGuardCard) {
      errors.push({
        field: 'newYork.hasGuardCard',
        message: i18n.t('validation.crossStep.nyGuardCardRequired'),
      });
    }

    if (hasArmedRole && hasFlLocations && !data.stepC.florida?.hasClassG) {
      errors.push({
        field: 'florida.hasClassG',
        message: i18n.t('validation.crossStep.flClassGRequired'),
      });
    }

    if (hasArmedRole && hasNyLocations && !data.stepC.newYork?.hasFirearmCreds) {
      errors.push({
        field: 'newYork.hasFirearmCreds',
        message: i18n.t('validation.crossStep.nyFirearmCredsRequired'),
      });
    }
  }

  return errors;
}

// Generic validation function
export function validateStep(
  step: string,
  data: ApplicationData,
): { success: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};

  switch (step) {
    case 'stepA': {
      const result = stepASchema.safeParse(data.stepA);
      if (!result.success) {
        for (const issue of result.error.issues) {
          const key = issue.path[0];
          if (typeof key === 'string') {
            errors[key] = issue.message;
          }
        }
      }
      break;
    }
    case 'stepB': {
      const result = stepBSchema.safeParse(data.stepB);
      if (!result.success) {
        for (const issue of result.error.issues) {
          const key = issue.path[0];
          if (typeof key === 'string') {
            errors[key] = issue.message;
          }
        }
      }
      break;
    }
    case 'stepC': {
      const stepCResult = validateStepC(data.stepC, data.stepA.state);
      return stepCResult;
    }
    case 'stepD': {
      const result = stepDSchema.safeParse(data.stepD);
      if (!result.success) {
        for (const issue of result.error.issues) {
          const key = issue.path[0];
          if (typeof key === 'string') {
            errors[key] = issue.message;
          }
        }
      }
      break;
    }
    case 'stepE': {
      const result = stepESchema.safeParse(data.stepE);
      if (!result.success) {
        for (const issue of result.error.issues) {
          const key = issue.path[0];
          if (typeof key === 'string') {
            errors[key] = issue.message;
          }
        }
      }
      break;
    }
    case 'stepF': {
      const result = stepFSchema.safeParse(data.stepF);
      if (!result.success) {
        for (const issue of result.error.issues) {
          const key = issue.path[0];
          if (typeof key === 'string') {
            errors[key] = issue.message;
          }
        }
      }
      break;
    }
    default:
      errors.step = i18n.t('validation.fields.invalidStep');
  }

  return {
    success: Object.keys(errors).length === 0,
    errors,
  };
}
